/**
 * Settings Index Route — /settings
 * Mobile: iOS-style grouped menu list.
 * Desktop: redirect to first accessible section.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useHasPermission, useUserPermissions } from '@/hooks/usePermissions'
import { REPORTS_SECTIONS, SECURITY_SECTIONS, DATA_SECTIONS, CALENDAR_SECTIONS, SYSTEM_SECTIONS, type SettingsSection, type SectionItem } from './settings-sections'
import styles from '@/pages/Settings.module.css'

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsIndex,
})

function SettingsIndex() {
  const navigate = useNavigate()
  const { isLoading: permissionsLoading } = useUserPermissions()
  const canManageSettings = useHasPermission('settings', 'manage')
  // Managers with tags:manage see the Attributes link for tag management (#903).
  const canManageTags = useHasPermission('tags', 'manage')

  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 769 : false
  )

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 769)
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  // Desktop: redirect to first accessible section (wait for permissions to resolve)
  useEffect(() => {
    if (!isMobile && !permissionsLoading) {
      const defaultSection: SettingsSection = canManageSettings ? 'general' : 'kpi'
      navigate({ to: '/settings/$section', params: { section: defaultSection }, replace: true })
    }
  }, [isMobile, permissionsLoading, canManageSettings, navigate])

  // Don't render mobile menu until permissions are resolved (avoids admin section flash).
  // The parent layout already shows nothing while loading, so users never see a blank screen.
  if (!isMobile || permissionsLoading) return null

  const renderGroup = (label: string, items: SectionItem[]) => {
    const visibleItems = items.filter(item =>
      !item.adminOnly || canManageSettings || (item.id === 'attributes' && canManageTags))
    if (visibleItems.length === 0) return null

    return (
      <div className={styles.menuGroup} key={label}>
        <span className={styles.menuGroupLabel}>{label}</span>
        <div className={styles.menuGroupItems}>
          {visibleItems.map(item => (
            <Link
              key={item.id}
              to="/settings/$section"
              params={{ section: item.id }}
              className={styles.menuItem}
            >
              <span className={styles.menuItemIcon}>{item.icon}</span>
              <span className={styles.menuItemLabel}>{item.label}</span>
              <span className={styles.menuItemChevron}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="9,18 15,12 9,6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </Link>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.menuPage}>
      <div className={styles.menuHeader}>
        <h1 className={styles.menuTitle}>Settings</h1>
        <p className={styles.menuSubtitle}>Manage your preferences and configuration</p>
      </div>
      {renderGroup('Reports', REPORTS_SECTIONS)}
      {renderGroup('Security', SECURITY_SECTIONS)}
      {renderGroup('Data', DATA_SECTIONS)}
      {renderGroup('Calendar', CALENDAR_SECTIONS)}
      {renderGroup('System', SYSTEM_SECTIONS)}
    </div>
  )
}
